import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { mutations, queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import { getTemplateFolders } from '../../../api/queries_v2';
import { FolderTreeSelector } from '../../../components/folder-tree-selector';
import PropdocsModal from '../../../components/modal/PropdocsModal';

type MoveClauseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
  teamId: string;
  onSuccess: () => void;
};

const MoveClauseModal = ({ isOpen, onClose, templateId, templateName, teamId, onSuccess }: MoveClauseModalProps) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // Fetch all clause folders to show as destination options
  const { data: foldersData, loading: foldersLoading } = useQuery(getTemplateFolders as any, {
    variables: { teamId, folderType: 'clause' },
    skip: !isOpen,
  });

  // Use the existing moveTemplateToFolder mutation
  const [moveTemplateToFolder] = useMutation(mutations.moveTemplateToFolder as any, {
    onCompleted: () => {
      notifySuccess({ message: 'Clause moved successfully' });
      onSuccess();
      onClose();
      setIsLoading(false);
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to move clause' });
      setIsLoading(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      await moveTemplateToFolder({
        variables: {
          templateId: templateId,
          folderId: selectedFolderId, // null for root level
        },
        refetchQueries: [
          {
            query: queriesV2.getTemplateFolders,
            variables: { teamId, folderType: 'clause' },
          },
          {
            query: queriesV2.getDocumentTemplates,
            variables: { teamId, templateType: 'clause' },
          },
        ],
      });
    } catch (error) {
      console.error('Error moving clause:', error);
      notifyError({ message: 'Failed to move clause' });
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFolderId(null);
    onClose();
  };

  // Filter to show only clause folders
  const availableFolders = foldersData?.getTemplateFolders || [];

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="lg"
      disableClose={isLoading}
      title={<FormattedMessage id="title.move-clause" defaultMessage="Move Clause" />}
    >
      <Form onSubmit={handleSubmit}>
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <div className="mb-3">
              <strong>
                <FormattedMessage
                  id="message.moving-clause"
                  defaultMessage="Moving: {templateName}"
                  values={{ templateName }}
                />
              </strong>
            </div>

            <Form.Group>
              <Form.Label>
                <FormattedMessage id="label.destination-folder" defaultMessage="Destination Folder" />
              </Form.Label>
              {foldersLoading ? (
                <div className="text-muted">
                  <FormattedMessage id="message.loading-folders" defaultMessage="Loading folders..." />
                </div>
              ) : (
                <FolderTreeSelector
                  folders={availableFolders}
                  selectedFolderId={selectedFolderId}
                  onSelectionChange={setSelectedFolderId}
                  disabled={isLoading}
                  context="clauses"
                />
              )}
            </Form.Group>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
            <Button variant="link" className="text-secondary" onClick={handleClose} disabled={isLoading}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading || foldersLoading}>
              {isLoading ? (
                <FormattedMessage id="cta.moving" defaultMessage="Moving..." />
              ) : (
                <TitleFormattedMessage id="cta.move" defaultMessage="Move" />
              )}
            </Button>
          </div>
        </>
      </Form>
    </PropdocsModal>
  );
};

export default MoveClauseModal;
